import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "./tour.css";

import { PAGE_TOURS, WELCOME_STEP } from "./tours";

// The driver.js engine behind "How it works" and the first-login welcome. What each page
// says lives in tours.js; this file only runs a list of steps.
//
// A step whose target is not on screen is dropped rather than pointed at nothing: an
// empty table, a wizard step not reached yet, or the rail on a phone, where it is
// off-canvas and the tour shows the menu button instead.

const visible = (selector) => {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 && r.right > 0 && r.left < window.innerWidth ? el : null;
};

const reveal = element => {
  if (!element?.scrollIntoView) return;
  const main = element.closest('.v2-main');
  const bar = main?.querySelector('.v2-builder-bar');
  const inset = bar && !bar.contains(element) && !element.contains(bar) ? bar.getBoundingClientRect().height + 12 : 12;
  const previous = element.style.scrollMarginTop;
  element.style.scrollMarginTop = `${inset}px`;
  // Do this before driver.js measures the target, including nested scroll containers.
  element.scrollIntoView({ behavior: 'instant', block: element.getBoundingClientRect().height > window.innerHeight / 2 ? 'start' : 'center', inline: 'nearest' });
  element.style.scrollMarginTop = previous;
};

/**
 * Runs a tour. Returns the driver instance, or null if there was nothing to show.
 * `onDone` runs once, however the tour ends — finished, skipped or closed.
 *
 * A tour is a list of steps, or `{ steps, setup }` where setup() runs first and returns a
 * function to run when the tour ends. On top of driver.js's own step fields, a step can
 * carry `before` — run as the tour arrives at it, to put the screen in the state the step
 * talks about (the lead magnet tour moves the wizard to the step it is describing) — and
 * `lazy`, for a target that only exists once `before` has run.
 */
function run(tourDef, { onDone } = {}) {
  const def = Array.isArray(tourDef) ? { steps: tourDef } : tourDef;

  // Resolved now rather than per step, so the progress count is honest: "3 of 9" should
  // not jump to "5 of 9" because two steps turned out to have nothing to point at.
  const plan = def.steps.filter((s) => !s.element || s.lazy || visible(s.element));
  if (!plan.length) return null;

  const teardown = def.setup ? def.setup() : null;

  // driver.js renders into <body>, outside #v2-root, so the v2 palette (scoped to .v2 and
  // .v2-tokens) would not reach it. Borrow the shell's tokens and theme class.
  const dark = document.getElementById("v2-root")?.classList.contains("dark");

  let ended = false;
  let tour = null;
  let sizeObserver = null;
  // Scroll events do not bubble. Capture them from the shell and settings scrollers
  // so the highlight and navigation follow the target inside either panel.
  const refresh = () => { if (!ended) tour?.refresh(); };
  let navigation = 0;

  // Moving between steps goes through here so a step's `before` can change the screen
  // and React can render it before driver.js measures the target.
  const goTo = (index, start = false) => {
    const step = plan[index];
    if (!step) { tour.destroy(); return; }
    const ticket = ++navigation;
    const show = () => {
      if (ended || ticket !== navigation) return;
      const element = step.element ? visible(step.element) : null;
      reveal(element);
      const move = () => {
        if (ended || ticket !== navigation) return;
        if (start) tour.drive(index); else tour.moveTo(index);
      };
      // Scrolling and a tab change must settle before positioning the highlight.
      if (element) requestAnimationFrame(move); else move();
    };
    if (step.before) {
      step.before();
      setTimeout(show, 120);
    } else {
      show();
    }
  };

  tour = driver({
    steps: plan.map(({ before, lazy, ...s }) => (
      // A lazy target that never appears gets a centred popover rather than a highlight
      // around nothing.
      s.element ? { ...s, element: () => visible(s.element) || undefined } : s
    )),
    showProgress: true,
    progressText: "{{current}} of {{total}}",
    nextBtnText: "Next",
    prevBtnText: "Back",
    doneBtnText: "Done",
    popoverClass: `v2-tokens v2-tour${dark ? " dark" : ""}`,
    overlayOpacity: 0.55,
    stagePadding: 6,
    stageRadius: 10,
    smoothScroll: false,
    onPopoverRender: ({ wrapper }) => {
      // Let the description shrink and scroll while keeping Back/Next in view.
      wrapper.style.display = 'flex';
    },
    onHighlighted: element => {
      sizeObserver?.disconnect();
      if (element) sizeObserver?.observe(element);
      refresh();
    },
    // Hovering a rail item opens its flyout, which would sit on top of the popover.
    disableActiveInteraction: true,
    onNextClick: () => {
      const i = tour.getActiveIndex() ?? 0;
      if (i >= plan.length - 1) tour.destroy();
      else goTo(i + 1);
    },
    onPrevClick: () => goTo(Math.max(0, (tour.getActiveIndex() ?? 0) - 1)),
    onDestroyed: () => {
      if (ended) return;
      ended = true;
      document.removeEventListener('scroll', refresh, true);
      sizeObserver?.disconnect();
      if (teardown) teardown();
      onDone && onDone();
    },
  });

  document.addEventListener('scroll', refresh, true);
  if (typeof ResizeObserver === 'function') sizeObserver = new ResizeObserver(refresh);

  goTo(0, true);
  return tour;
}

/** The tour of one screen, for its How it works button. */
export const startPageTour = (key, opts) => (PAGE_TOURS[key] ? run(PAGE_TOURS[key], opts) : null);

/** The first-login welcome: the dashboard's tour, opened with a greeting. */
export const startWelcomeTour = ({ firstName, ...opts } = {}) =>
  run([WELCOME_STEP(firstName), ...PAGE_TOURS.dashboard.slice(1)], opts);
